import { useEffect, useState, useRef } from "react";

/**
 * usePWAInstall — captures `beforeinstallprompt` and exposes a tiny
 * API for components to:
 *   - know if the app is already installed (running standalone)
 *   - know if the browser can prompt for install (Android Chrome)
 *   - know if the platform is iOS (needs manual instructions)
 *   - call `prompt()` to fire the native install prompt
 *
 * One global listener keeps the deferred event alive so any component
 * can call it. Returns null-safe values so SSR/Node won't crash.
 */
const useDeferredPromptRefSingleton = (() => {
  let stored = null;
  let listeners = new Set();
  if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      stored = e;
      listeners.forEach((l) => l(e));
    });
    window.addEventListener("appinstalled", () => {
      stored = null;
      try { localStorage.setItem("avi_pwa_installed", "1"); } catch { /* ignore */ }
      listeners.forEach((l) => l(null));
    });
  }
  return {
    subscribe(fn) {
      listeners.add(fn);
      fn(stored);
      return () => listeners.delete(fn);
    },
    consume() {
      const e = stored;
      stored = null;
      listeners.forEach((l) => l(null));
      return e;
    },
  };
})();

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true ||
    document.referrer.startsWith("android-app://")
  );
};

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const usePWAInstall = () => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const unsubscribe = useDeferredPromptRefSingleton.subscribe((e) => {
      setDeferred(e);
    });
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      unsubscribe();
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const canPrompt = !installed && !!deferred;
  const platform = isIOS() ? "ios" : "android";

  const prompt = async () => {
    const e = useDeferredPromptRefSingleton.consume();
    if (!e) return { outcome: "unavailable" };
    try {
      await e.prompt();
      const choice = await e.userChoice;
      return { outcome: choice.outcome };
    } catch (err) {
      return { outcome: "error", error: err?.message };
    }
  };

  return { installed, canPrompt, platform, prompt };
};

export default usePWAInstall;
